/**
 * WaveEndScreen - End-of-wave bonus tally
 * Animates point accumulation for surviving cities and remaining ammo,
 * then transitions to the next wave intro.
 */

export class WaveEndScreen {
  constructor(game) {
    this.game = game;
    this.wave = 1;
    this.survivingCities = 0;
    this.remainingAmmo = 0;
    this.cityBonus = 0;
    this.ammoBonus = 0;
    this.displayedCityBonus = 0;
    this.displayedAmmoBonus = 0;
    this.timer = 0;
    this.totalTimer = 0; // total time in screen for animation
    this.phase = 'cities'; // 'cities' -> 'ammo' -> 'total' -> 'done'
    this.scoreBeforeBonus = 0;
    this._tickAccum = 0; // Accumulator for score tick sound timing

    // Starfield for visual polish
    this.stars = [];
    this._initStars(80);
  }

  _initStars(count) {
    this.stars = [];
    for (let i = 0; i < count; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 1.5 + 0.3,
        brightness: Math.random() * 0.5 + 0.2,
        twinkleSpeed: Math.random() * 2 + 1,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
  }

  enter(data = {}) {
    this.wave = data.wave || 1;
    this.survivingCities = data.survivingCities || 0;
    this.remainingAmmo = data.remainingAmmo || 0;
    this.cityBonus = data.cityBonus || this.survivingCities * 100 * this.wave;
    this.ammoBonus = data.ammoBonus || this.remainingAmmo * 5 * this.wave;
    this.playingState = data.playingState || null;
    this.scoreBeforeBonus = this.playingState ? this.playingState.score : 0;
    this.displayedCityBonus = 0;
    this.displayedAmmoBonus = 0;
    this.timer = 0;
    this.totalTimer = 0;
    this.phase = 'cities';

    // Update overlay
    this._updateOverlay();
  }

  exit() {
    if (this.game.uiOverlay) {
      this.game.uiOverlay.innerHTML = '';
    }
  }

  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    const totalBonus = Math.floor(this.cityBonus + this.ammoBonus);
    const displayedTotal = Math.floor(this.displayedCityBonus + this.displayedAmmoBonus);

    this.game.uiOverlay.innerHTML = `
      <div data-screen="waveEnd">
        <div class="wave-complete" data-wave="${this.wave}">WAVE ${this.wave} COMPLETE</div>
        <div class="city-bonus" data-cities="${this.survivingCities}" data-city-bonus="${Math.floor(this.cityBonus)}" data-displayed-city-bonus="${Math.floor(this.displayedCityBonus)}">Cities Saved: ${this.survivingCities} (+${Math.floor(this.displayedCityBonus)})</div>
        <div class="ammo-bonus" data-ammo="${this.remainingAmmo}" data-ammo-bonus="${Math.floor(this.ammoBonus)}" data-displayed-ammo-bonus="${Math.floor(this.displayedAmmoBonus)}">Ammo Remaining: ${this.remainingAmmo} (+${Math.floor(this.displayedAmmoBonus)})</div>
        <div class="total-bonus" data-total-bonus="${totalBonus}" data-displayed-total="${displayedTotal}">Total Bonus: ${displayedTotal}</div>
        <div class="phase" data-phase="${this.phase}">Phase: ${this.phase}</div>
        <div class="score-before" data-score-before="${this.scoreBeforeBonus}">Score Before: ${this.scoreBeforeBonus}</div>
      </div>
    `;
  }

  update(dt) {
    this.timer += dt;
    this.totalTimer += dt;

    // Animate tally with score tick sounds
    if (this.phase === 'cities') {
      const prevCity = Math.floor(this.displayedCityBonus);
      this.displayedCityBonus = Math.min(this.displayedCityBonus + dt * 500, this.cityBonus);
      // Play tick sound periodically during counting
      this._tickAccum += dt;
      if (this._tickAccum >= 0.08 && Math.floor(this.displayedCityBonus) > prevCity && this.game.audio) {
        this.game.audio.play('score_tick');
        this._tickAccum = 0;
      }
      if (this.displayedCityBonus >= this.cityBonus) {
        this.phase = 'ammo';
        this._tickAccum = 0;
      }
    } else if (this.phase === 'ammo') {
      const prevAmmo = Math.floor(this.displayedAmmoBonus);
      this.displayedAmmoBonus = Math.min(this.displayedAmmoBonus + dt * 500, this.ammoBonus);
      this._tickAccum += dt;
      if (this._tickAccum >= 0.08 && Math.floor(this.displayedAmmoBonus) > prevAmmo && this.game.audio) {
        this.game.audio.play('score_tick');
        this._tickAccum = 0;
      }
      if (this.displayedAmmoBonus >= this.ammoBonus) {
        this.phase = 'total';
        this.timer = 0;
      }
    } else if (this.phase === 'total') {
      if (this.timer > 2) {
        // Add bonus to score and advance wave via PlayingState
        const totalBonus = Math.floor(this.cityBonus + this.ammoBonus);
        if (this.playingState) {
          this.playingState.advanceWave(totalBonus);
        } else {
          this.game.state.setState('waveIntro', { wave: this.wave + 1 });
        }
      }
    }

    // Update overlay every frame
    this._updateOverlay();
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    this.game.renderer.clearBackground();

    // Starfield background
    this._renderStarfield(ctx, w, h);

    // Fade-in effect
    const fadeIn = Math.min(this.totalTimer / 0.5, 1);

    // Wave complete header with pulsing glow
    const headerPulse = 1 + Math.sin(this.totalTimer * 3) * 0.15;
    ctx.save();
    ctx.globalAlpha = fadeIn;
    this.game.renderer.drawGlowText(`WAVE ${this.wave} COMPLETE`, w / 2, h * 0.12, {
      font: `${Math.max(28, w * 0.04)}px "Share Tech Mono", monospace`,
      color: '#00ff88',
      glowColor: '#00ff88',
      glowSize: 15 * headerPulse,
    });

    // Separator line below header
    ctx.strokeStyle = 'rgba(0, 255, 136, 0.3)';
    ctx.lineWidth = 1;
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur = 5;
    ctx.beginPath();
    ctx.moveTo(w * 0.25, h * 0.19);
    ctx.lineTo(w * 0.75, h * 0.19);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // --- City bonus section ---
    this.game.renderer.drawGlowText('CITIES SAVED', w / 2, h * 0.28, {
      font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
      color: '#aabbcc',
      glowColor: '#445566',
      glowSize: 3,
    });

    // City count with warm amber glow
    this.game.renderer.drawGlowText(`${this.survivingCities}`, w / 2, h * 0.34, {
      font: `${Math.max(32, w * 0.04)}px "Share Tech Mono", monospace`,
      color: '#ffcc66',
      glowColor: '#ffaa00',
      glowSize: 12,
    });

    // City bonus amount (animated)
    const cityBonusDisplay = Math.floor(this.displayedCityBonus);
    const cityBonusAlpha = cityBonusDisplay > 0 ? 1 : 0.3;
    ctx.globalAlpha = fadeIn * cityBonusAlpha;
    this.game.renderer.drawGlowText(`+${cityBonusDisplay}`, w / 2, h * 0.41, {
      font: `${Math.max(22, w * 0.028)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#ffaa00',
      glowSize: cityBonusDisplay > 0 ? 10 : 3,
    });
    ctx.globalAlpha = fadeIn;

    // Formula label
    this.game.renderer.drawGlowText(`(${this.survivingCities} × 100 × Wave ${this.wave})`, w / 2, h * 0.46, {
      font: `${Math.max(12, w * 0.015)}px "Share Tech Mono", monospace`,
      color: '#8899aa',
      glowColor: '#445566',
      glowSize: 2,
    });

    // --- Ammo bonus section (appears after city tally) ---
    if (this.phase !== 'cities') {
      const ammoFadeIn = Math.min((this.displayedAmmoBonus > 0 ? 1 : (this.phase === 'ammo' ? 0.8 : 1)), 1);
      ctx.globalAlpha = fadeIn * ammoFadeIn;

      // Separator
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(w * 0.3, h * 0.52);
      ctx.lineTo(w * 0.7, h * 0.52);
      ctx.stroke();

      this.game.renderer.drawGlowText('AMMO REMAINING', w / 2, h * 0.56, {
        font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
        color: '#aabbcc',
        glowColor: '#445566',
        glowSize: 3,
      });

      this.game.renderer.drawGlowText(`${this.remainingAmmo}`, w / 2, h * 0.62, {
        font: `${Math.max(32, w * 0.04)}px "Share Tech Mono", monospace`,
        color: '#00ffff',
        glowColor: '#0088ff',
        glowSize: 12,
      });

      // Ammo bonus amount (animated)
      const ammoBonusDisplay = Math.floor(this.displayedAmmoBonus);
      this.game.renderer.drawGlowText(`+${ammoBonusDisplay}`, w / 2, h * 0.69, {
        font: `${Math.max(22, w * 0.028)}px "Share Tech Mono", monospace`,
        color: '#ffffff',
        glowColor: '#00ffff',
        glowSize: ammoBonusDisplay > 0 ? 10 : 3,
      });

      // Formula label
      this.game.renderer.drawGlowText(`(${this.remainingAmmo} × 5 × Wave ${this.wave})`, w / 2, h * 0.74, {
        font: `${Math.max(12, w * 0.015)}px "Share Tech Mono", monospace`,
        color: '#8899aa',
        glowColor: '#445566',
        glowSize: 2,
      });
      ctx.globalAlpha = fadeIn;
    }

    // --- Total bonus (appears after both tallies complete) ---
    if (this.phase === 'total' || this.phase === 'done') {
      const total = Math.floor(this.cityBonus + this.ammoBonus);
      const totalPulse = 1 + Math.sin(this.timer * 4) * 0.1;

      // Separator
      ctx.strokeStyle = 'rgba(0, 255, 136, 0.3)';
      ctx.lineWidth = 1;
      ctx.shadowColor = '#00ff88';
      ctx.shadowBlur = 4;
      ctx.beginPath();
      ctx.moveTo(w * 0.2, h * 0.82);
      ctx.lineTo(w * 0.8, h * 0.82);
      ctx.stroke();
      ctx.shadowBlur = 0;

      this.game.renderer.drawGlowText(`TOTAL BONUS: +${total}`, w / 2, h * 0.88, {
        font: `${Math.max(24, w * 0.032)}px "Share Tech Mono", monospace`,
        color: '#ffffff',
        glowColor: '#00ff88',
        glowSize: 18 * totalPulse,
      });

      // Current score display
      const newScore = this.scoreBeforeBonus + total;
      this.game.renderer.drawGlowText(`SCORE: ${newScore}`, w / 2, h * 0.94, {
        font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
        color: '#aabbcc',
        glowColor: '#0088ff',
        glowSize: 5,
      });
    }

    ctx.restore();
  }

  _renderStarfield(ctx, w, h) {
    for (const star of this.stars) {
      const twinkle = 0.5 + 0.5 * Math.sin(this.totalTimer * star.twinkleSpeed + star.twinklePhase);
      const alpha = star.brightness * (0.5 + 0.5 * twinkle);

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      if (star.size > 1.0) {
        ctx.shadowColor = '#ffffff';
        ctx.shadowBlur = star.size * 2;
      }
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
